import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Section, Cell, Spinner, Caption } from "@telegram-apps/telegram-ui";
import { api } from "../../lib/api.js";
import { usePageBack } from "../../lib/telegram.js";

const PAGE_SIZE = 100;

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("ru-RU"); } catch { return iso; }
}

function pilotLabel(m) {
  const name = [m.first_name, m.last_name].filter(Boolean).join(" ");
  if (name) return m.tg_username ? `${name} (@${m.tg_username})` : name;
  if (m.tg_username) return `@${m.tg_username}`;
  if (m.tg_user_id) return `tg:${m.tg_user_id}`;
  if (m.user_id) return `#${m.user_id}`;
  return "—";
}

function actorLabel(m) {
  if (!m.actor_user_id) return null;
  const name = [m.actor_first_name, m.actor_last_name].filter(Boolean).join(" ");
  if (name) return m.actor_username ? `${name} (@${m.actor_username})` : name;
  if (m.actor_username) return `@${m.actor_username}`;
  return `#${m.actor_user_id}`;
}

export default function MessagesList() {
  usePageBack("/superadmin");
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const userId = params.get("user_id") || "";
  const direction = params.get("direction") || "";
  const kind = params.get("kind") || "";
  const q = params.get("q") || "";
  const offset = Number(params.get("offset") || 0);

  const [draft, setDraft] = useState(q);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setData(null);
    const qs = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
    if (userId) qs.set("user_id", userId);
    if (direction) qs.set("direction", direction);
    if (kind) qs.set("kind", kind);
    if (q) qs.set("q", q);
    api(`/api/v1/superadmin/messages?${qs}`).then(setData).catch((e) => setError(e.message));
  }, [userId, direction, kind, q, offset]);

  function patch(next) {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v == null || v === "") merged.delete(k);
      else merged.set(k, String(v));
    }
    merged.delete("offset");
    setParams(merged, { replace: true });
  }

  const submitSearch = (e) => {
    e?.preventDefault?.();
    patch({ q: draft });
  };

  return (
    <>
      <section className="portal-card">
        <header className="portal-card__header">
          <h2 className="portal-card__title">Сообщения бота</h2>
          {data && <span className="portal-card__hint">На странице: {data.messages.length}</span>}
        </header>

        <form onSubmit={submitSearch} style={{ display: "grid", gap: 10, gridTemplateColumns: "2fr 1fr 1fr" }}>
          <div className="portal-search">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="7" cy="7" r="5" /><path d="m11 11 3 3" strokeLinecap="round" />
            </svg>
            <input
              type="search"
              placeholder="Поиск по тексту"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={submitSearch}
            />
          </div>
          <select
            value={direction}
            onChange={(e) => patch({ direction: e.target.value })}
            style={selectStyle}
          >
            <option value="">Все направления</option>
            <option value="in">📥 От пилота</option>
            <option value="out">📤 От бота</option>
          </select>
          <select
            value={kind}
            onChange={(e) => patch({ kind: e.target.value })}
            style={selectStyle}
          >
            <option value="">Все типы</option>
            {data?.kinds?.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </form>

        {(userId || q || direction || kind) && (
          <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
            {userId && (
              <FilterChip label={`пилот #${userId}`} onClear={() => patch({ user_id: "" })} />
            )}
            {direction && (
              <FilterChip label={`направление: ${direction === "in" ? "от пилота" : "от бота"}`}
                onClear={() => patch({ direction: "" })} />
            )}
            {kind && <FilterChip label={`тип: ${kind}`} onClear={() => patch({ kind: "" })} />}
            {q && <FilterChip label={`«${q}»`} onClear={() => { setDraft(""); patch({ q: "" }); }} />}
          </div>
        )}
      </section>

      <Section header={data ? `Записей на странице: ${data.messages.length}` : "Загрузка…"}>
        {error && <Cell multiline>Ошибка: {error}</Cell>}
        {!data && !error && <Cell><Spinner size="m" /></Cell>}
        {data?.messages?.map((m) => {
          const actor = actorLabel(m);
          const subParts = [formatDate(m.created_at)];
          subParts.push(m.direction === "in" ? "📥 от пилота" : "📤 от бота");
          subParts.push(m.kind);
          if (m.airfield_name) subParts.push(m.airfield_name);
          if (actor) subParts.push(`админ: ${actor}`);
          if (m.send_error) subParts.push(`⚠ ${m.send_error}`);
          return (
            <Cell
              key={m.id}
              multiline
              subtitle={subParts.join(" · ")}
              onClick={m.user_id ? () => navigate(`/superadmin/users/${m.user_id}`) : undefined}
              after={m.user_id ? "→" : null}
            >
              <strong>{pilotLabel(m)}</strong>
              {m.text && (
                <>
                  <br />
                  <span style={{ whiteSpace: "pre-wrap", color: "var(--portal-text)" }}>
                    {truncate(m.text, 600)}
                  </span>
                </>
              )}
            </Cell>
          );
        })}
        {data && data.messages.length === 0 && (
          <Cell><Caption level="1">Сообщений нет.</Caption></Cell>
        )}
      </Section>

      <div className="portal-pager">
        <button
          type="button"
          className="portal-pager__btn"
          disabled={offset === 0}
          onClick={() => {
            const next = new URLSearchParams(params);
            next.set("offset", String(Math.max(0, offset - PAGE_SIZE)));
            setParams(next, { replace: true });
          }}
        >← Назад</button>
        <span>записей: {data?.messages.length ?? 0}</span>
        <button
          type="button"
          className="portal-pager__btn"
          disabled={!data || data.messages.length < PAGE_SIZE}
          onClick={() => {
            const next = new URLSearchParams(params);
            next.set("offset", String(offset + PAGE_SIZE));
            setParams(next, { replace: true });
          }}
        >Дальше →</button>
      </div>
    </>
  );
}

const selectStyle = {
  font: "inherit", fontSize: 14, padding: "10px 14px",
  background: "var(--portal-surface)", color: "var(--portal-text)",
  border: "1px solid var(--portal-border)", borderRadius: 10, outline: "none",
};

function FilterChip({ label, onClear }) {
  return (
    <button
      type="button"
      onClick={onClear}
      style={{
        font: "inherit", fontSize: 13,
        padding: "4px 10px",
        background: "var(--portal-surface)",
        color: "var(--portal-text)",
        border: "1px solid var(--portal-border)",
        borderRadius: 999, cursor: "pointer",
      }}
    >
      {label} ✕
    </button>
  );
}

function truncate(s, n) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n) + "…" : s;
}
